import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * FilterPopup component
 * A reusable popup for filtering projects with alphabetical navigation
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Handler for closing the dialog
 * @param {Function} props.onApply - Handler for applying the filters
 * @param {Array} props.projects - List of projects to filter
 * @param {Array} props.selectedProjects - List of selected project IDs
 * @param {Function} props.onProjectSelectionChange - Handler for project selection change
 * @param {string} props.searchValue - Current search value
 * @param {Function} props.onSearchChange - Handler for search input change
 * @returns {React.ReactElement} FilterPopup component
 */
const FilterPopup = ({
  open,
  onClose,
  onApply,
  projects = [],
  selectedProjects = [],
  onProjectSelectionChange,
  searchValue = '',
  onSearchChange,
  onAlphabetChange,
  initialAlphabet = 'All'
}) => {
  // State for internal alphabet filter
  const [activeAlphabet, setActiveAlphabet] = useState(initialAlphabet);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchValue);
console.log(projects);
  // Initialize the filtered projects and reset state when dialog opens
  useEffect(() => {
    if (open) {
      // Clear all previous state and start fresh
      setFilteredProjects(projects);
      setSearchTerm(''); // Always start with empty search
      setActiveAlphabet('All'); // Always start with 'All' selected

      // Force reset of selectedProjects through the parent component
      if (onProjectSelectionChange) {
        onProjectSelectionChange([]);
      }

      // Log the complete reset
      console.log('Filter dialog opened: Complete reset of filter state');
      console.log(`Showing all ${projects?.length || 0} projects in the filter dialog`);
    }
  }, [open, onProjectSelectionChange]);

  // This effect updates filtered projects whenever projects or alphabet changes
  // It's critical to ensure we always show the latest data with the current filter
  useEffect(() => {
    if (projects && projects.length > 0) {
      if (activeAlphabet === 'All') {
        setFilteredProjects(projects);
        console.log(`Showing all ${projects.length} projects (no alphabet filter)`);
      } else {
        // Apply current alphabet filter to the projects
        const filtered = projects.filter(project =>
          project.name.toUpperCase().startsWith(activeAlphabet)
        );
        setFilteredProjects(filtered);
        console.log(`Applied alphabet filter '${activeAlphabet}': ${filtered.length} projects shown`);
      }
    }
  }, [projects, activeAlphabet]);

  // Handle alphabet filtering
  const handleAlphabetClick = (letter) => {
    setActiveAlphabet(letter);

    if (letter === 'All') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project =>
        project.name.toUpperCase().startsWith(letter)
      );
      setFilteredProjects(filtered);
    }

    // If there's an onAlphabetChange prop, call it
    if (onAlphabetChange) {
      onAlphabetChange(letter);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Filter projects based on search term
    if (value.trim() === '') {
      // If search is empty, reset to alphabet filter
      handleAlphabetClick(activeAlphabet);
    } else {
      const filtered = projects.filter(project =>
        project.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProjects(filtered);
    }
  };

  // Handle select all checkbox change
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all filtered projects
      const newSelectedProjects = [...selectedProjects];
      filteredProjects.forEach(project => {
        if (!newSelectedProjects.includes(project._id)) {
          newSelectedProjects.push(project._id);
        }
      });
      onProjectSelectionChange(newSelectedProjects);
    } else {
      // Deselect all filtered projects
      const newSelectedProjects = selectedProjects.filter(id => 
        !filteredProjects.some(project => project._id === id)
      );
      onProjectSelectionChange(newSelectedProjects);
    }
  };

  // Handle individual project selection
  const handleProjectSelection = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      onProjectSelectionChange(selectedProjects.filter(id => id !== projectId));
    } else {
      onProjectSelectionChange([...selectedProjects, projectId]);
    }
  };

  // Check if all filtered projects are selected
  const areAllFilteredProjectsSelected = () => {
    return filteredProjects.length > 0 && 
      filteredProjects.every(project => selectedProjects.includes(project._id));
  };

  // Handle apply button click
  const handleApply = () => {
    // Call the parent's onSearchChange with the final search term
    if (onSearchChange) {
      onSearchChange(searchTerm);
    }

    // Call the parent's onApply handler with all filter data
    onApply({
      selectedProjects,
      searchTerm,
      activeAlphabet
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '4px',
          boxShadow: '0 0 10px rgba(0,0,0,0.05)',
          maxWidth: '90vw',
          width: '90vw',
          maxHeight: '90vh',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          pb: '14px',
          pt: '14px',
          pl: 5,
          pr: '10px',
          bgcolor: 'white'
        }}
      >
        <Typography variant="h6" color="#475569" fontWeight={500}>
          Filters
        </Typography>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            color: '#475569',
            bgcolor: '#f1f5f9',
            borderRadius: '50%',
            '&:hover': {
              bgcolor: '#e2e8f0'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ px: 5, py: 3, mt: 3, display: 'flex', flexDirection: 'column', height: 'calc(100% - 140px)' }}>
        {/* Fixed Controls Section */}
        <Box sx={{ position: 'sticky', top: 0, bgcolor: 'white', zIndex: 10 }}>
          {/* Search Field */}
          <Box sx={{ mb: 3 }}>
            <Typography
              component="label"
              variant="body2"
              sx={{
                mb: 1,
                display: 'block',
                color: 'var(--color-blue, #475969)',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Search
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                placeholder="Search projects"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue, #475969)',
                    height: '45px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                      borderRadius: '4px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: 'var(--color-blue, #475969)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px',
                  }
                }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#485869',
                  color: 'white',
                  borderRadius: '5px',
                  px: 3,
                  height: '45px',
                  fontWeight: 400,
                  textTransform: 'none',
                  minWidth: '120px',
                  '&:hover': {
                    bgcolor: '#3A4754'
                  },
                  boxShadow: 'none',
                  border: '1px solid #485869',
                }}
                onClick={() => {
                  if (onSearchChange) {
                    onSearchChange(searchTerm);
                  }
                }}
              >
                Search
              </Button>
            </Box>
          </Box>

          {/* Projects Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Checkbox
              checked={true}
              disabled
              size="small"
              sx={{
                p: 0.5,
                mr: 1,
                color: '#475569'
              }}
            />
            <Typography fontWeight={600} color="#475569">
              Projects
            </Typography>
          </Box>

          {/* Alphabet Filter */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              mb: 2.5,
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              p: 1
            }}
          >
            <Button
              variant={activeAlphabet === 'All' ? 'contained' : 'text'}
              size="small"
              onClick={() => handleAlphabetClick('All')}
              sx={{
                minWidth: 32,
                height: 28,
                fontSize: '13px',
                padding: '0px 8px',
                borderRadius: '0px',
                fontWeight: 500,
                bgcolor: activeAlphabet === 'All' ? '#475569' : 'transparent',
                color: activeAlphabet === 'All' ? 'white' : '#475569',
                '&:hover': {
                  bgcolor: activeAlphabet === 'All' ? '#334155' : '#f1f5f9'
                }
              }}
            >
              All
            </Button>
            {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(letter => (
              <Button
                key={letter}
                variant={activeAlphabet === letter ? 'contained' : 'text'}
                size="small"
                onClick={() => handleAlphabetClick(letter)}
                sx={{
                  minWidth: 30,
                  height: 28,
                  fontSize: '13px',
                  padding: '0px 8px',
                  borderRadius: '0px',
                  fontWeight: 500,
                  bgcolor: activeAlphabet === letter ? '#475569' : 'transparent',
                  color: activeAlphabet === letter ? 'white' : '#475569',
                  '&:hover': {
                    bgcolor: activeAlphabet === letter ? '#334155' : '#f1f5f9'
                  }
                }}
              >
                {letter}
              </Button>
            ))}
          </Box>

          {/* Select All (Fixed) */}
          <Box sx={{
            borderBottom: '1px solid #e2e8f0',
            pb: 1.5,
            mb: 1.5
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={areAllFilteredProjectsSelected()}
                  onChange={handleSelectAll}
                  size="small"
                  sx={{
                    color: '#94a3b8',
                    '&.Mui-checked': {
                      color: '#475569',
                    }
                  }}
                />
              }
              label="Select All"
              sx={{
                '& .MuiTypography-root': {
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#334155'
                }
              }}
            />
          </Box>
        </Box>

        {/* Project Selection List - Scrollable */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          px: 0.5,
             // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
        }}>
          <FormGroup>
            <Grid container spacing={1}>
              {filteredProjects.map(project => (
                <Grid item xs={12} sm={6} md={4} key={project._id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedProjects.includes(project._id)}
                        onChange={() => handleProjectSelection(project._id)}
                        size="small"
                        sx={{
                          color: '#94a3b8',
                          '&.Mui-checked': {
                            color: '#475569',
                          }
                        }}
                      />
                    }
                    label={project.name}
                    sx={{
                      mb: 0,
                      '& .MuiTypography-root': {
                        fontSize: '14px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '180px',
                        color: '#334155'
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            {filteredProjects.length === 0 && (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No projects found
                </Typography>
              </Box>
            )}
          </FormGroup>
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2, 
          borderTop: '1px solid #e5e7eb',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            bgcolor: '#d35a98',
            color: 'white',
            borderRadius: '8px',
            px: 3,
            py: 0.75,
            fontSize: '14px',
            fontWeight: 'normal',
            textTransform: 'none',
            minWidth: '100px',
            '&:hover': {
              bgcolor: '#c44b89'
            }
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterPopup;